import {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {CreateQuest, ExtendedQuest, Quest, QuestWithQuestType, UpdateQuest, UserQuest} from "../types";


type FetchQuestMethod = (quiet?: boolean, refresh?: boolean) => Promise<Quest>;
type PushQuestMethod = (updates: UpdateQuest | CreateQuest) => Promise<Quest>;
type DeleteQuestMethod = () => Promise<void>;
type CompleteQuestMethod = (creatorData?: Record<string, unknown>) => Promise<UserQuest>;
export interface UseQuestReturn {
  quest: Quest | null;
  fetched: boolean;
  refresh: FetchQuestMethod;
  push: PushQuestMethod;
  delete: DeleteQuestMethod;
  complete: CompleteQuestMethod;
}

export const useQuest = (id?: string): UseQuestReturn => {
  const {
    completeQuest,
    createQuest,
    updateQuest,
    deleteQuest: deleteQuestApi,
    getQuest
  } = useApi();

  const [quest, setQuest] = useState<Quest | null>(null);
  const [fetched, setFetched] = useState<boolean>(!id);

  const fetchQuest: FetchQuestMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getQuest", [id]);

    try {
      if(!id) throw new Error("No quest id provided");

      const quest = await withCache(getQuest, [id], 120_000);
      setQuest(quest);

      return quest;
    } catch (error) {
      console.error(`Error fetching quest ${id}`, error);
      throw new Error(`Error fetching quest ${id}`, {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const pushQuest: PushQuestMethod = async updatedQuest => {
    const newQuest = {...quest, ...updatedQuest};

    if(!newQuest) throw new Error("No quest to push");

    try {
      const quest = id ? await updateQuest(id, newQuest) : await createQuest(newQuest);

      if(!quest) throw new Error("Failed to push quest");

      setQuest(quest);

      clearCache("getQuest", [quest.id]);
      clearCache("getQuests");

      return quest;
    } catch (error) {
      console.error("Error pushing quest", error);
      throw new Error("Error pushing quest", {cause: error});
    }
  };

  const deleteQuest: DeleteQuestMethod = async() => {
    if(!quest?.id) throw new Error("No quest to delete");

    try {
      deleteQuestApi(quest.id);
      clearCache("getQuest", [quest.id]);
      clearCache("getQuests");
    } catch (error) {
      console.error("Error deleting quest", error);
      throw new Error("Error deleting quest", {cause: error});
    }
  };

  const complete: CompleteQuestMethod = async creatorData => {
    if(!quest?.id) throw new Error("No quest to complete");

    try {
      const questId = quest.id;
      const updatedQuest = await completeQuest(questId, creatorData);

      clearCache("getUserQuests");
      clearCache("getUserQuestsByQuest");
      clearCache("getUserQuestsByProject");

      return updatedQuest;
    } catch (error) {
      console.error(`Error completing quest '${quest.id}'`, error);
      throw new Error(`Error completing quest '${quest.id}'`, {cause: error});
    }
  };

  useEffect(() => {
    if(id) fetchQuest();
  }, [id]);

  return {
    complete,
    delete: deleteQuest,
    fetched,
    push: pushQuest,
    quest,
    refresh: quiet => fetchQuest(quiet, true)
  };
};


type FetchQuestsMethod = (quiet?: boolean, refresh?: boolean) => Promise<ExtendedQuest[]>;
type DeleteQuestsMethod = (id: string) => Promise<void>;
type PushQuestsMethod = (id: string, quest: UpdateQuest) => Promise<Quest>;
type CompleteQuestsMethod = (id: string, creatorData?: Record<string, unknown>) => Promise<UserQuest>;
interface UseQuestsReturn {
  quests: ExtendedQuest[];
  fetched: boolean;
  refresh: FetchQuestsMethod

  // @deprecated use quests instead
  questList: ExtendedQuest[];
}

export const useQuests = (params: {[key: string]: any} = {}): UseQuestsReturn => {
  const {
    completeQuest,
    getQuests,
    deleteQuest: deleteQuestApi,
    updateQuest: updateQuestApi
  } = useApi();

  const [quests, setQuests] = useState<ExtendedQuest[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);

  const extendQuests = (quests: QuestWithQuestType[]): ExtendedQuest[] => quests.map(quest => ({
    ...quest,
    complete: creatorData => complete(quest.id, creatorData),
    delete: () => deleteQuest(quest.id),
    push: updates => pushQuest(quest.id, updates)
  }));

  // Fetch the vote status of submissionId from the backend
  const fetchQuests: FetchQuestsMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getQuests");

    try {
      const quests = extendQuests(await withCache(getQuests, [params], 120_000));
      setQuests(quests);
      return quests;
    } catch (e) {
      console.error("Error fetching quests", e);
      throw new Error("Error fetching quests", {cause: e});
    } finally {
      setFetched(true);
    }
  };

  const updateQuestState = (
    questId: string,
    updates: UpdateQuest
  ) => {
    setQuests(prevQuests =>
      prevQuests.map(quest =>
        quest.id === questId ? {...quest, ...updates} : quest
      )
    );
  };

  const pushQuest: PushQuestsMethod = async(id, quest) => {
    try {
      const newQuest = await updateQuestApi(id, quest);
      if(!newQuest) throw new Error("Quest not uploaded");
      updateQuestState(id, newQuest);
      clearCache("getQuests");
      return newQuest;
    } catch (error) {
      console.error(`Error pushing quest '${id}'`, error);
      throw new Error(`Error pushing quest '${id}'`, {cause: error});
    }
  };

  const deleteQuest: DeleteQuestsMethod = async(id: string) => {
    try {
      await deleteQuestApi(id);
      setQuests(prevQuests =>
        prevQuests.filter(quest => quest.id !== id)
      );
      clearCache("getQuests");
    } catch (error) {
      console.error(`Error deleting quest '${id}'`, error);
      throw new Error(`Error deleting quest '${id}'`, {cause: error});
    }
  };

  const complete: CompleteQuestsMethod = async(questId, creatorData) => {
    try {
      const userQuestOrError = await completeQuest(questId, creatorData);

      clearCache("getUserQuests");
      clearCache("getUserQuestsByQuest");
      clearCache("getUserQuestsByProject");

      return userQuestOrError;
    } catch (error) {
      console.error(`Error completing quest '${questId}'`, error);
      throw new Error(`Error completing quest '${questId}'`, {cause: error});
    }
  };

  useEffect(() => {
    fetchQuests();
  }, [JSON.stringify(params)]);

  return {
    fetched,
    questList: quests,
    quests,
    refresh: quiet => fetchQuests(quiet, true)
  };
};
